<template>
  <div class="container-fluid">
    <controls
      ref="controlsRef"
      :currentUser="currentUser"
      :selectedCalendarDate="selectedCalendarDate"
      :aircraft="aircraft"
      :instructors="instructors"
      :rooms="rooms"
      :eventTypes="eventTypes"
      :userCanCreateReservations="userCanCreateReservations"
      :userCanAccessSettings="userCanAccessSettings"
      :standbyFlightsExist="standbyFlightsExist"
      :allEvents="events"
      :events="filteredEvents"
      :filteredAircraft="filteredAircraft"
      :filteredInstructors="filteredInstructors"
      :filteredRooms="filteredRooms"
      @update-date="getEvents"
      @update-aircraft-filter="updateAircraftFilter"
      @update-instructor-filter="updateInstructorFilter"
      @update-room-filter="updateRoomFilter"
      @update-type-filter="updateEventTypeFilter"
      @reset-schedule-filters="resetFilters"
      @show-reservation-modal="showReservationModal(false)"
      @show-standby-flights="showAllFlights"
      @show-modal="showEventDetailModal"
    ></controls>

    <event-detail-modal
      v-if="isEventDetailModalVisible"
      :currentUser="currentUser"
      :event="selectedEvent"
      :eventTypes="eventTypes"
      :eventStatusOptions="eventStatusOptions"
      :aircraft="aircraft"
      :instructors="instructors"
      :rooms="rooms"
      :customers="customers"
      @edit-event="hideEventDetailModal(true)"
      @check-in="showCheckInModal"
      @event-status-updated="hideAndUpdateStatus"
      @close="hideEventDetailModal"
    ></event-detail-modal>

    <reservation-modal
      v-if="isReservationModalVisible"
      ref="reservation-modal"
      :event="selectedEvent"
      @event-created="handleEventCreated"
      @event-updated="handleEventUpdated"
      @event-deleted="handleEventDeleted"
      @close="hideReservationModal"
    ></reservation-modal>

    <check-in-modal
      v-if="isCheckInModalVisible"
      :event="selectedEvent"
      @check-in-complete="saveDataFromCheckIn"
      @close="hideCheckInModal"
    ></check-in-modal>

    <!--event-tooltip
      :event="tooltipEvent"
      :visible="tooltipVisible"
      :position="tooltipPosition"
      :aircraft="aircraft"
      :instructors="instructors"
      :rooms="rooms"
      :customers="customers"
      :eventTypes="eventTypes"
      :eventStatusOptions="eventStatusOptions"
    /-->
  </div>
</template>

<script>
import Controls from "./Schedule/Controls.vue";
import EventDetailModal from "./Schedule/EventDetailModal.vue";
// import EventTooltip from "./Schedule/EventTooltip.vue";
import ReservationModal from "./Schedule/ReservationModal.vue";
import EventsAPIService from "../../../servicehandlers/EventsAPIService.js";
import AircraftAPIService from "../../../servicehandlers/AircraftAPIService.js";
import ActiveInstructorsAPIService from "../../../servicehandlers/ActiveInstructorsAPIService.js";
import ActiveStudentsAPIService from "../../../servicehandlers/ActiveStudentsAPIService.js";
import EventRoomsAPIService from "../../../servicehandlers/EventRoomsAPIService.js";
import EventTypesAPIService from "../../../servicehandlers/EventTypesAPIService.js";
import { useLoginStore } from "@/stores/loginStore";
import { pullObjectInfo } from "@/pages/Dashboard/HiloPages/Schedule/scheduleUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CheckInModal from "@/pages/Dashboard/HiloPages/Schedule/CheckInModal.vue";

dayjs.extend(utc);

const aircraftAPIService = new AircraftAPIService();
const activeInstructorsAPIService = new ActiveInstructorsAPIService();
const activeStudentsAPIService = new ActiveStudentsAPIService();
const eventsAPIService = new EventsAPIService();
const eventRoomsAPIService = new EventRoomsAPIService();
const eventTypesAPIService = new EventTypesAPIService();

export default {
  components: {
    CheckInModal,
    Controls,
    EventDetailModal,
    //EventTooltip,
    ReservationModal,
  },
  data() {
    return {
      selectedCalendarDate: {}, // #DATE
      currentUser: {},
      selectedAircraft: "all",
      selectedInstructors: "all",
      selectedRooms: "all",
      selectedEventTypes: "all",
      aircraft: [],
      instructors: [],
      rooms: [],
      students: [],
      customers: [],
      events: [],
      filteredEvents: [],
      eventTypes: [],
      eventStatusOptions: [],
      selectedEvent: null,
      selectedItem: null,
      selectedInstructor: null,
      selectedRoom: null,
      selectedEventType: null,
      tooltipVisible: false,
      tooltipPosition: { top: 0, left: 0 },
      tooltipEvent: null,
      windowWidth: null,
      hourWidth: null,
      userCanCreateReservations: false,
      userCanAccessSettings: false,
      standbyFlightsExist: false,
      isReservationModalVisible: false,
      isEventDetailModalVisible: false,
      isCheckInModalVisible: false,
    };
  },
  async mounted() {
    const user = useLoginStore().getUser;
    this.currentUser = {
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      userType: user.userType?.name,
    };
    if (useLoginStore().isAdmin) {
      this.userCanCreateReservations = true;
      this.userCanAccessSettings = true;
    }
    await Promise.all([
      this.getEventTypes(),
      this.getEventStatusOptions(),
      this.getAircraft(),
      this.getInstructors(),
      this.getStudents(),
      this.getEvents(),
      this.getRooms(),
      // this.updateEventDatesForDemo(),
    ]);
  },
  beforeMount() {
    this.initSelectedCalendarDate();
    this.updateWidth();
  },
  computed: {
    filteredAircraft() {
      if (this.selectedAircraft === "all") {
        return this.aircraft;
      } else if (this.selectedAircraft === "none") {
        return [];
      } else if (this.selectedAircraft === "scheduled") {
        return this.aircraft.filter(
          (item) =>
            this.getEventsForAircraft(item.id).length > 0 &&
            !this.getEventsForAircraft(item.id).some(
              (event) => event.displayName === "Unavailable",
            ),
        );
      } else if (this.selectedAircraft === "available") {
        return this.aircraft.filter(
          (item) => this.getEventsForAircraft(item.id).length === 0,
        );
      }
      return this.aircraft.filter(
        (item) => item.id.toString() === this.selectedAircraft.toString(),
      );
    },
    filteredInstructors() {
      if (this.selectedInstructors === "all") {
        return this.instructors;
      } else if (this.selectedInstructors === "none") {
        return [];
      } else if (this.selectedInstructors === "scheduled") {
        return this.instructors.filter(
          (instructor) =>
            this.getEventsForInstructor(instructor.id).length > 0 &&
            !this.getEventsForInstructor(instructor.id).some(
              (event) => event.displayName === "Unavailable",
            ),
        );
      } else if (this.selectedInstructors === "available") {
        return this.instructors.filter(
          (instructor) =>
            this.getEventsForInstructor(instructor.id).length === 0,
        );
      }
      return this.instructors.filter(
        (instructor) =>
          instructor.id.toString() === this.selectedInstructors.toString(),
      );
    },
    filteredRooms() {
      if (this.selectedRooms === "all") {
        return this.rooms;
      } else if (this.selectedRooms === "none") {
        return [];
      } else if (this.selectedRooms === "scheduled") {
        return this.rooms.filter(
          (room) =>
            this.getEventsForRoom(room.id).length > 0 &&
            !this.getEventsForRoom(room.id).some(
              (event) => event.displayName === "Unavailable",
            ),
        );
      } else if (this.selectedRooms === "available") {
        return this.rooms.filter(
          (room) => this.getEventsForRoom(room.id).length === 0,
        );
      }
      return this.rooms.filter(
        (room) => room.id.toString() === this.selectedRooms.toString(),
      );
    },
    filteredEventTypes() {
      if (this.selectedEventTypes === "all") {
        return this.filteredEvents;
      }
      return this.filteredEvents.filter(
        (event) =>
          event.eventTypeId.toString() === this.selectedEventTypes.toString(),
      );
    },
    calendarDateSelectedIsToday() {
      return dayjs(this.selectedCalendarDate).isSame(dayjs(), "day");
    },
  },
  methods: {
    // GETTERS
    async getEventStatusOptions() {
      return eventsAPIService
        .getStatusOptions(this.$router)
        .then((response) => {
          this.eventStatusOptions = response;
        })
        .catch(() => {});
    },
    async getEventTypes() {
      return eventTypesAPIService
        .get(this.$router)
        .then((response) => {
          this.eventTypes = response;
        })
        .catch(() => {});
    },
    async getAircraft() {
      return aircraftAPIService
        .get(this.$router)
        .then((response) => {
          this.aircraft = response.map((item) => {
            return {
              ...item,
              aircraftStatus: item.aircraftStatus?.name,
              name: `${item.tailNumber} ${item.model}`,
            };
          });
        })
        .catch(() => {});
    },
    async getInstructors() {
      return activeInstructorsAPIService
        .get(this.$router)
        .then((response) => {
          this.instructors = response;
          for (let instructor of this.instructors) {
            instructor.firstName = instructor.user.firstName;
            instructor.lastName = instructor.user.lastName;
            instructor.name = `${instructor.user.firstName} ${instructor.user.lastName}`;
          }
        })
        .catch(() => {});
    },
    async getStudents() {
      return activeStudentsAPIService
        .get(this.$router)
        .then((response) => {
          this.students = response;
          for (let student of this.students) {
            student.firstName = student.user.firstName;
            student.lastName = student.user.lastName;
            student.name = `${student.user.firstName} ${student.user.lastName}`;
          }
          this.customers = [...this.students];
        })
        .catch(() => {});
    },
    async getEvents(newDate) {
      const parsedDate = newDate
        ? dayjs(newDate).startOf("day")
        : dayjs().startOf("day");
      this.selectedCalendarDate = parsedDate;

      const startOfTodayUTC = parsedDate.utc().format();
      try {
        const events = await eventsAPIService.getEventsByDate(
          startOfTodayUTC,
          this.$router,
        );
        this.events = this.processEventsForCalendar(events, parsedDate);
        this.standbyFlightsExist = this.events.some(
          (event) => event.eventStatus?.name === "Standby",
        );
        this.filteredEvents = this.events.filter(
          (event) => event.eventStatus?.name !== "Standby",
        );
      } catch (error) {}
    },
    toggleReservationPopUp() {
      this.isAdminNewClassPopUpOpen = !this.isAdminNewClassPopUpOpen;
      this.lockScrolling();
    },
    processEventsForCalendar(events, selectedDate) {
      const dailyEvents = [];
      const selectedDay = dayjs(selectedDate).startOf("day");
      const displayStartHour = 5;
      const displayEndHour = 24;

      events.forEach((event) => {
        const eventStart = dayjs(event.startDateTime);
        const eventEnd = dayjs(event.endDateTime);

        const eventOverlapsVisibleWindow =
          eventEnd.isAfter(selectedDay.set("hour", displayStartHour)) &&
          eventStart.isBefore(selectedDay.set("hour", displayEndHour));

        if (eventOverlapsVisibleWindow) {
          const displayStartDateTime = eventStart.isBefore(
            selectedDay.set("hour", displayStartHour),
          )
            ? selectedDay.set("hour", displayStartHour).toISOString()
            : eventStart.toISOString();

          const displayEndDateTime = eventEnd.isAfter(
            selectedDay.set("hour", displayEndHour),
          )
            ? selectedDay.set("hour", displayEndHour).toISOString()
            : eventEnd.toISOString();

          if (dayjs(displayStartDateTime).isBefore(displayEndDateTime)) {
            dailyEvents.push({
              ...event,
              displayStartDateTime,
              displayEndDateTime,
            });
          }
        }
      });

      return dailyEvents;
    },
    async getRooms() {
      return eventRoomsAPIService
        .get(this.$router)
        .then((response) => {
          this.rooms = response;
        })
        .catch(() => {});
    },

    // SETTERS
    handleEventCreated(created) {
      this.events.push(created);
      this.hideReservationModal();
      this.resetFilters();
    },
    handleEventUpdated(updated) {
      const index = this.events.findIndex((event) => event.id === updated.id);
      if (index !== -1) {
        this.events.splice(index, 1, updated);
      }
      this.hideReservationModal();
      this.resetFilters();
    },
    handleEventDeleted(deleted) {
      const index = this.events.findIndex((event) => event.id === deleted.id);
      if (index !== -1) {
        this.events.splice(index, 1);
      }
      this.hideReservationModal();
      this.resetFilters();
    },
    initSelectedCalendarDate() {
      this.selectedCalendarDate = dayjs(); //#DATE
    },
    updateWidth() {
      this.windowWidth = window.innerWidth - 525; // leaves room for margins and stuff
      this.hourWidth = this.windowWidth / 19; // #WDH Remember to modify timeToMinutes in scheduleUtils.js if you change this, otherwise ScheduleGrid will be out of sync with EventGrid and CurrentTimeIndicator.
    },
    // EVENTS
    getEventsForAircraft(planeId) {
      return this.filteredEventTypes.filter(
        (event) => event.aircraftId === planeId,
      );
    },
    getEventsForInstructor(instructorId) {
      return this.filteredEventTypes.filter(
        (event) => event.instructorId === instructorId,
      );
    },
    getEventsForRoom(roomId) {
      return this.filteredEventTypes.filter((event) => event.roomId === roomId);
    },
    updateAircraftFilter(selected) {
      this.selectedAircraft = selected;
    },
    updateInstructorFilter(selected) {
      this.selectedInstructors = selected;
    },
    updateRoomFilter(selected) {
      this.selectedRooms = selected;
    },
    updateEventTypeFilter(selected) {
      this.selectedEventTypes = selected;
    },
    resetFilters() {
      this.filteredEvents = this.events.filter(
        (event) => event.eventStatus?.name !== "Standby",
      );
      this.selectedAircraft = "all";
      this.selectedInstructors = "all";
      this.selectedRooms = "all";
      this.selectedEventTypes = "all";

      this.$refs.controlsRef.resetShortcutButtons();
    },
    showAllFlights() {
      this.filteredEvents = this.events;
    },
    showTooltip(event, position) {
      this.tooltipEvent = event;
      this.tooltipPosition = position;
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },

    // MODALS
    showCheckInModal(event) {
      this.isEventDetailModalVisible = false;
      this.selectedEvent = event;
      this.isCheckInModalVisible = true;
    },
    hideCheckInModal() {
      this.isCheckInModalVisible = false;
    },
    showEventDetailModal(event, item, instructor, room) {
      this.selectedEvent = event;
      this.selectedItem = item;
      this.selectedInstructor = instructor;
      this.selectedRoom = room;
      this.isEventDetailModalVisible = true;
    },
    hideEventDetailModal(openEdit = false) {
      this.isEventDetailModalVisible = false;
      if (openEdit === true) {
        this.showReservationModal(true);
      }
    },
    async saveDataFromCheckIn(event) {
      this.hideCheckInModal();

      const checkedInStatus = this.eventStatusOptions.find(
        (status) => status.name === "Checked In",
      );

      try {
        if (event.aircraft) {
          const aircraftUpdateData = {
            hobbs: event.checkInInfo.hobbs,
            ...(event.checkInInfo.tach && { tach: event.checkInInfo.tach }),
          };
          await aircraftAPIService.update(
            event.aircraft.id,
            aircraftUpdateData,
          );
        }

        await this.hideAndUpdateStatus(event, checkedInStatus.id);
      } catch (error) {}
    },
    async hideAndUpdateStatus(event, status) {
      event.eventStatusId = status;
      let statusName = pullObjectInfo(this.eventStatusOptions, status).name;
      this.hideEventDetailModal();
      try {
        const updateEntry = {
          ...this.currentUser,
          action: "Update",
          changes: `<div style="float: left;">Changed status to <strong style="color: #3171e2;">${statusName}</strong></div><br/>`,
        };
        let temp = {
          event: event,
          history: updateEntry,
        };
        const updated = await eventsAPIService.update(
          event.id,
          temp,
          this.$router,
        );
        updated.startDateTime = dayjs(updated.startDateTime)
          .local()
          .format("YYYY-MM-DDTHH:mm"); // #DATE Format to local before db response completes
        updated.endDateTime = dayjs(updated.endDateTime)
          .local()
          .format("YYYY-MM-DDTHH:mm"); // #DATE Format to local before db response completes
        this.handleEventUpdated(updated);
        await this.getAircraft();
        this.toast(
          "b-toaster-bottom-right",
          `Successfully changed status to ${statusName}!`,
          "success",
          true,
        );
      } catch (error) {}
    },
    toast(toaster, message, variant, append = false) {
      this.$bvToast.toast(`${message}`, {
        title: `Success`,
        toaster: toaster,
        solid: true,
        variant: variant,
        appendToast: append,
        autoHideDelay: 2000,
      });
    },
    showReservationModal(isEdit) {
      if (!isEdit) {
        this.selectedEvent = null;
      }
      this.isReservationModalVisible = true;
    },
    hideReservationModal() {
      this.isReservationModalVisible = false;
      this.getEvents(this.selectedCalendarDate);
    },
  },
};
</script>

<style scoped></style>

<style lang="css">
/*noinspection CssUnusedSymbol */
.modal-body {
  background-color: #ececec;
  border: none;
  padding: 0;
}
/*noinspection CssUnusedSymbol */
.modal-header {
  background-color: #ececec;
  color: black;
  border: none;
}
/*noinspection CssUnusedSymbol */
.modal-title {
  font-size: 18pt;
}
</style>
